import React from 'react'
import {
  Box,
  LinearProgress,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
} from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material'

export interface ProgressStep {
  id: string
  label: string
  status: 'pending' | 'running' | 'completed' | 'error' | 'warning'
  message?: string
  progress?: number // 0-100
  startTime?: Date
  endTime?: Date
}

export interface ProgressOperation {
  id: string
  title: string
  description?: string
  status: 'pending' | 'running' | 'completed' | 'error' | 'cancelled'
  progress: number // 0-100
  steps: ProgressStep[]
  startTime: Date
  endTime?: Date
  totalItems?: number
  processedItems?: number
  successCount?: number
  errorCount?: number
  warningCount?: number
  canCancel?: boolean
  canRetry?: boolean
  onCancel?: () => void
  onRetry?: () => void
  onClose?: () => void
}

interface ProgressIndicatorProps {
  operation: ProgressOperation
  variant?: 'compact' | 'detailed'
  showSteps?: boolean
  autoClose?: boolean
  autoCloseDelay?: number
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  operation,
  variant = 'detailed',
  showSteps = true,
  autoClose = false,
  autoCloseDelay = 5000,
}) => {
  const [expanded, setExpanded] = React.useState(false)
  const [visible, setVisible] = React.useState(true)

  // Auto close on completion
  React.useEffect(() => {
    if (autoClose && operation.status === 'completed' && !operation.errorCount) {
      const timer = setTimeout(() => {
        setVisible(false)
        operation.onClose?.()
      }, autoCloseDelay)
      return () => clearTimeout(timer)
    }
  }, [operation.status, operation.errorCount, autoClose, autoCloseDelay, operation])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success'
      case 'error': return 'error'
      case 'warning': return 'warning'
      case 'running': return 'info'
      case 'cancelled': return 'default'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckIcon color="success" />
      case 'error': return <ErrorIcon color="error" />
      case 'warning': return <WarningIcon color="warning" />
      case 'running': return <CircularProgress size={20} />
      default: return <InfoIcon color="info" />
    }
  }

  const formatDuration = (start: Date, end?: Date) => {
    const endTime = end || new Date()
    const duration = Math.floor((endTime.getTime() - start.getTime()) / 1000)
    
    if (duration < 60) return `${duration}s`
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`
  }

  if (!visible) return null

  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          mb: 2,
        }}
      >
        {getStatusIcon(operation.status)}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {operation.title}
          </Typography>
          {operation.status === 'running' && (
            <LinearProgress 
              variant="determinate" 
              value={operation.progress} 
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {operation.progress}%
        </Typography>
        {operation.onClose && (
          <IconButton size="small" onClick={operation.onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    )
  }

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getStatusIcon(operation.status)}
            <Box>
              <Typography variant="h6" component="div">
                {operation.title}
              </Typography>
              {operation.description && (
                <Typography variant="body2" color="text.secondary">
                  {operation.description}
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={operation.status.toUpperCase()}
              color={getStatusColor(operation.status) as any}
              size="small"
            />
            {operation.onClose && (
              <IconButton size="small" onClick={operation.onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Progress Bar */}
        {operation.status === 'running' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Progress: {operation.processedItems || 0} / {operation.totalItems || 0}
              </Typography>
              <Typography variant="body2">
                {operation.progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={operation.progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Summary Stats */}
        {(operation.successCount !== undefined || operation.errorCount !== undefined) && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {operation.successCount !== undefined && operation.successCount > 0 && (
              <Chip
                label={`${operation.successCount} successful`}
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            {operation.errorCount !== undefined && operation.errorCount > 0 && (
              <Chip
                label={`${operation.errorCount} errors`}
                color="error"
                size="small"
                variant="outlined"
              />
            )}
            {operation.warningCount !== undefined && operation.warningCount > 0 && (
              <Chip
                label={`${operation.warningCount} warnings`}
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Timing Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Started: {operation.startTime.toLocaleTimeString()}
            {operation.endTime && ` • Duration: ${formatDuration(operation.startTime, operation.endTime)}`}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {operation.canCancel && operation.status === 'running' && (
              <Button
                size="small"
                color="error"
                onClick={operation.onCancel}
              >
                Cancel
              </Button>
            )}
            {operation.canRetry && operation.status === 'error' && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={operation.onRetry}
              >
                Retry
              </Button>
            )}
          </Box>
        </Box>

        {/* Steps Detail */}
        {showSteps && operation.steps.length > 0 && (
          <>
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mb: 1 }}
            >
              {expanded ? 'Hide' : 'Show'} Details ({operation.steps.length} steps)
            </Button>
            
            <Collapse in={expanded}>
              <List dense>
                {operation.steps.map((step) => (
                  <ListItem key={step.id} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getStatusIcon(step.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={step.label}
                      secondary={step.message}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: step.status === 'error' ? 'error' : 'text.primary'
                      }}
                    />
                    {step.progress !== undefined && (
                      <Box sx={{ width: 60, ml: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={step.progress}
                          sx={{ height: 4 }}
                        />
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Error Alert */}
        {operation.status === 'error' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Operation failed. Please check the details above and try again.
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default ProgressIndicator